import { useOAndM } from "@features/ax/assetRegistry/assets/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { Plus, Wrench } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface AssetMaintenanceTabProps {
  data: {
    assetName?: string;
    assetId?: string;
    location: string;
    systemsNetworks?: string[];
  };
}

export const NetworkServiceOrdersTab: React.FC<AssetMaintenanceTabProps> = ({
  data,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: oAndM } = useOAndM(id, search);
  useEffect(() => {
    logEvent("o&m_tab_viewed");
  }, []);

  const getPriorityColor = (priority: string): string => {
    switch (priority.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      case "critical":
        return "bg-red-200 text-red-900";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string): string => {
    switch (type?.toLowerCase()) {
      case "preventive":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "corrective":
        return "bg-orange-50 text-orange-700 border-orange-200";
      case "inspection":
        return "bg-green-50 text-green-700 border-green-200";
      case "emergency":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case "in progress":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "scheduled":
        return "bg-gray-100 text-gray-800";
      case "active":
        return "bg-orange-100 text-orange-800";
      case "overdue":
        return "bg-red-100 text-red-800";
      case "cancelled":
        return "bg-red-50 text-red-700";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getServiceTypeFromAssociation = (
    serviceAssociation: string
  ): string => {
    const association = serviceAssociation;

    if (association?.includes("Asset")) return "Asset";
    if (association?.includes("Facility")) return "Facility";
    if (association?.includes("System")) return "System";
    if (association?.includes("Network")) return "Network";

    // Default fallback
    return "Asset";
  };

  const handleViewDetails = (row: any) => {
    const serviceType = getServiceTypeFromAssociation(
      row.sopAssociationTypeDisplay
    );
    const status = row.statusDisplay?.toLowerCase();

    // Determine the route based on status
    let routePrefix = "";

    switch (status) {
      case "created":
      case "overdue":
        routePrefix = "pending";
        break;
      case "assigned":
      case "accepted":
      case "in-progress":
      case "in progress":
        routePrefix = "assigned";
        break;
      case "completed":
      case "rejected":
        routePrefix = "completed";
        break;
      case "canceled":
      case "cancelled":
      case "approved":
        routePrefix = "history";
        break;
      default:
        // Default to pending for unknown statuses
        routePrefix = "pending";
    }

    navigate(
      `/ax/service-order/landing/${routePrefix}/view/${serviceType}/${row.id}`
    );
  };

  const columns: TableColumn[] = [
    {
      key: "jobCardNo",
      header: "Order ID",
      type: "custom",
      headerTooltip: "The unique identifier for this work order.",
      render: (value) => <span className="font-medium ml-3">{value}</span>,
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      type: "text",
      headerTooltip: "A brief description of the work to be performed.",
      render: (value) => <span className="font-medium ml-6">{value}</span>,
    },
    {
      key: "requestName",
      header: "Name / Ref ID",
      type: "custom",
      headerTooltip: "The name or reference ID of the work order.",
      render: (value, record) => (
        <div>
          <div className="font-medium">{record.requestUsername ?? "NA"}</div>
          <div className="text-sm text-muted-foreground">
            {record.requestAccountNo ?? "NA"}
          </div>
        </div>
      ),
    },
    {
      key: "priorityDisplay",
      header: "Priority",
      type: "custom",
      render: (value: string) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
            value
          )}`}
        >
          {value}
        </span>
      ),
      headerTooltip:
        "Indicates the urgency level of the service order (e.g., Low, Medium, High).",
    },
    {
      key: "sopOperationTypeDisplay",
      header: "Order Type",
      type: "custom",
      render: (value: string) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(
            value
          )}`}
        >
          {value}
        </span>
      ),
      headerTooltip:
        "The type of entity the order is for (Asset, System, Facility, Network).",
    },
    {
      key: "fieldForceName",
      header: "Assigned To",
      type: "text",
      headerTooltip: "The field force member assigned to the order.",
    },
    {
      key: "createdDate",
      header: "Created On",
      type: "text",
      headerTooltip: "The date the service order was created.",
    },
    {
      key: "expectedEndDate",
      header: "Due Date",
      type: "text",
      headerTooltip: "The target completion date.",
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "custom",
      render: (value: string) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
            value
          )}`}
        >
          {value}
        </span>
      ),
      headerTooltip: "The current status of the service order.",
    },
  ];

  const actions = [
    {
      label: "View",
      icon: "Eye" as const,
      onClick: handleViewDetails,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <Wrench className="h-5 w-5 text-blue-600" />
          </div>
          <PageHeader
            title="Service Orders"
            subtitle="Manage and track all maintenance service orders and work requests"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate("/ax/service-order/landing/create")}
          >
            <Plus className="h-4 w-4" />
            Create Order
          </Button>
        </div>
      </div>
      <GenericTable
        data={oAndM?.results ?? []}
        columns={columns}
        rowKey="id"
        actions={actions}
        actionsType="icons"
        searchPlaceholder="Search maintenance records..."
        onSearch={(searchTerm) => {
          setSearch(searchTerm);
        }}
        emptyMessage="No maintenance records found"
      />
    </div>
  );
};
